// Contrat temps réel entre le client de jeu et le serveur (Socket.io).
import type { Emote, ScreenEffect } from './scenes.ts';
import { dominantElement, type Element, type ElementType, type Typed } from './elements.ts';
import type { IconName } from './icons.ts';
import type { CharacterDesign, CharacterOutfit } from './characters.ts';
import type { MapMonsterView } from './map-monsters.ts';
import type { BuffZone } from './buff-zones.ts';
import type { MapElement, MapBorder, MapObject, MapOuterTile, MapRotation } from './map-elements.ts';
import type { EquipmentAbility, Item, ItemId } from './items.ts';
import type { LightSource } from './lighting.ts';
import type { Sound } from './sounds.ts';
import type { Voice } from './voices.ts';
import type { Weather } from './weather.ts';
import type { ItemRewards, QuestRepeat } from './quests.ts';
import type { AchievementView } from './achievements.ts';
import type { BoxSort } from './game-rules.ts';
import type { Rarity } from './rarity.ts';
import type { WorldMapView } from './world-map.ts';
import type { BaseStats, StatBonus } from './stats.ts';
import type { BattleAnimation, BattleStat, Move, MoveAnimation, StatusCondition } from './moves.ts';
import type { GameConfig } from './game-config.ts';
import type { MonsterSprite } from './monster-sprites.ts';
import { isKeyOf } from './guards.ts';

export type Direction = 'up' | 'down' | 'left' | 'right';

export const DIRECTION_VECTORS: Readonly<Record<Direction, readonly [number, number]>> = {
  up: [0, -1],
  down: [0, 1],
  left: [-1, 0],
  right: [1, 0],
};

export const DIRECTIONS = Object.keys(DIRECTION_VECTORS) as Direction[];

export const isDirection = isKeyOf<Direction>(DIRECTION_VECTORS);

const OPPOSITE_DIRECTIONS: Readonly<Record<Direction, Direction>> = { up: 'down', down: 'up', left: 'right', right: 'left' };

/** Direction opposée (demi-tour). */
export const oppositeDirection = (dir: Direction): Direction => OPPOSITE_DIRECTIONS[dir];

/** Case située juste devant une position, dans la direction du regard. */
export function facingCell({ x, y }: Position, dir: Direction): Position {
  const [dx, dy] = DIRECTION_VECTORS[dir];
  return { x: x + dx, y: y + dy };
}

export interface Position {
  x: number;
  y: number;
}

export interface PlayerView extends Position {
  id: number;
  username: string;
  color: string;
  dir: Direction;
  /** Apparence choisie (null : personnage dessiné par défaut). */
  characterId: number | null;
  /** Tenue portée, pour une apparence personnalisable (null : celle par défaut de l'apparence). */
  outfit: CharacterOutfit | null;
  /** Le dernier pas était couru (animation et cadence de course). */
  running: boolean;
  /** Monstre qui suit le joueur, visible par tous. */
  follower: FollowerView | null;
  /** Capacités de ses équipements (des patins : il ne glisse pas sur la glace). */
  abilities: EquipmentAbility[];
  /** Lumière de son équipement (casque-lampe…), vue de tous ; null : aucune. */
  light: LightSource | null;
  /** {Monstre} sur lequel il surfe (il est dans l'eau, assis dessus) ; null : à pied. */
  surf: FollowerView | null;
}

export interface FollowerView {
  name: string;
  image: string;
  sprite: MonsterSprite | null;
  shiny: boolean;
  /** Taille sur la carte de son espèce, en pixels des planches (voir MonsterSpecies.mapSize). */
  size: number;
}

/** Marqueur au-dessus d'un PNJ : quête disponible, quête à rendre, dresseur pas encore battu. */
export type NpcMarker = 'quest' | 'turnin' | 'errand' | 'trainer';

export interface NpcView extends Position {
  id: number;
  name: string;
  color: string;
  dir: Direction;
  characterId: number | null;
  outfit: CharacterOutfit | null;
  marker: NpcMarker | null;
  /** Acteur de scène qui est un monstre : dessiné comme un monstre posé, à la taille de son espèce (voir MapMonsterView.size). */
  monster?: FollowerView;
}

export interface MapView {
  id: number;
  name: string;
  width: number;
  height: number;
  tiles: number[];
  /** Calques de terrain du dessus (EMPTY_TILE : case vide). */
  upperTiles: number[][];
  /** Identifiant de zone de farm par case (0 = aucune). */
  zones: number[];
  zoneNames: Record<number, string>;
  /** Multiplicateur de chance de chromatique des zones qui en ont un (absent : normal). */
  zoneShinyBoosts: Record<number, number>;
  /** Objets posés par-dessus le terrain. */
  objects: MapObject[];
  /** Sens de saut des bordures. */
  borders: MapBorder[];
  /** Cases de terrain tournées. */
  rotations: MapRotation[];
  /** Terrain de la marge autour de la carte : seulement pour les raccords au bord (et ce qui en déborde). */
  outerTiles: MapOuterTile[];
  /** Zones à effets qui en ont (vitesse des joueurs…). */
  buffZones: Pick<BuffZone, 'buffs' | 'cells'>[];
  /** Obscurité permanente (0 à 1), et la nuit l'assombrit-elle (voir darknessOf). */
  darkness: number;
  dayNight: boolean;
  weather: Weather;
  /** Musique et ambiance de la carte (sons de l'onglet Sons ; null : aucune). */
  music: number | null;
  ambience: number | null;
  /** Monstres posés que ce joueur n'a pas encore capturés ni vaincus. */
  monsters: MapMonsterView[];
  /** Réglages « Utiliser objet » (`MapObject.use.id`) qu'une quête en cours de ce joueur lui demande d'utiliser. */
  usable: string[];
  /** Objets posés (voir `objectSpotKey`) qu'une quête en cours de ce joueur lui demande d'examiner. */
  inspectable: string[];
  /** Interrupteurs que ce joueur a allumés (identifiants de leur mécanisme, voir mechanisms.ts) ; tous éteints en arrivant. */
  switchesOn: string[];
}

export interface OwnedMonsterView {
  id: number;
  speciesId: number;
  /** Surnom s'il y en a un, sinon nom de l'espèce. */
  name: string;
  speciesName: string;
  nickname: string | null;
  element: Element;
  /** Second type (null : il n'en a qu'un). */
  secondElement: Element | null;
  shiny: boolean;
  image: string;
  sprite: MonsterSprite | null;
  rarity: Rarity;
  level: number;
  xp: number;
  xpNext: number;
  hp: number;
  maxHp: number;
  atk: number;
  def: number;
  spd: number;
  /** Position dans l'équipe (0..5), ou null si le monstre est dans la boîte. */
  slot: number | null;
  /** Case dans la boîte du PC (null : dans l'équipe). */
  boxSlot: number | null;
  /** C'est le monstre qui suit le joueur. */
  following: boolean;
  /** Statistiques de base de l'espèce, et bonus propres à ce monstre (0 à BONUS_MAX). */
  base: BaseStats;
  bonus: StatBonus;
  /** Potentiel (0 à 5 étoiles) d'après ses bonus (voir bonusStars). */
  stars: number;
  caughtAt: number;
  /** Niveau auquel son espèce évolue, s'il y en a un. */
  evolveLevel: number | null;
  /** Ses attaques (4 au plus, identifiants), et toutes celles qu'il peut connaître à son niveau. */
  moves: number[];
  learnable: number[];
}

/** Adversaire affiché en combat : monstre sauvage, ou monstre actif d'un autre dresseur en duel. */
export interface WildMonsterView {
  name: string;
  image: string;
  sprite: MonsterSprite | null;
  rarity: Rarity;
  element: Element;
  /** Second type (null : il n'en a qu'un). */
  secondElement: Element | null;
  shiny: boolean;
  /** Le joueur a déjà capturé un monstre de cette espèce (une capsule s'affiche à côté du nom). */
  caught: boolean;
  level: number;
  hp: number;
  maxHp: number;
  status: StatusCondition | null;
  /** Énergie restante (sur ENERGY_MAX). */
  energy: number;
}

export type BattleMode = 'wild' | 'trainer' | 'duel';

/** Monstres d'une équipe en combat, combien sont encore debout, et son type dominant (couleur de l'écran « VS »). */
export interface TeamCount {
  size: number;
  alive: number;
  element: Element | null;
}

export const teamCount = (team: readonly ({ hp: number } & Typed)[]): TeamCount =>
  ({ size: team.length, alive: team.filter((fighter) => fighter.hp > 0).length, element: dominantElement(team) });

/** Camp vu par le joueur qui reçoit le message : son monstre (ally) ou l'adversaire (foe). */
export type BattleSide = 'ally' | 'foe';

/** Une place du terrain vue par le joueur : son camp, et la place (0, ou 1 en combat double). */
export interface BattleSlot {
  target: BattleSide;
  place: number;
}

/** Effet visuel associé à une étape du combat, rejoué au moment où son texte s'affiche. */
export type StepEffect =
  /** Un monstre entre en scène, avec ses PV à cet instant et ses crans (gardés d'un passage précédent sur le terrain). */
  | ({ type: 'appear'; hp: number; stages?: Partial<Record<BattleStat, number>> } & BattleSlot)
  /** `weather` : la météo de la carte a renforcé le coup. */
  | ({ type: 'damage'; hp: number; effectiveness: number; critical: boolean; weather: boolean } & BattleSlot)
  | ({ type: 'heal'; hp: number } & BattleSlot)
  /**
   * Le monstre de cette place lance une attaque de ce type (puissance 0 : attaque de statut), et il lui reste `energy` ;
   * `aim` : le monstre visé (null : une attaque sur lui-même).
   */
  | ({ type: 'move'; element: Element; power: number; energy: number; animation: MoveAnimation | null; aim: BattleSlot | null } & BattleSlot)
  /** Le monstre de cette place esquive l'attaque. */
  | ({ type: 'dodge' } & BattleSlot)
  /** L'attaque n'a aucun effet sur le monstre de cette place (type ou statut). */
  | ({ type: 'immune' } & BattleSlot)
  /** Le monstre de cette place se repose : il a maintenant `energy`. */
  | ({ type: 'rest'; energy: number } & BattleSlot)
  /** Une statistique du monstre de cette place augmente (rise) ou baisse ; `stages` : son cran désormais (-6 à +6). */
  | ({ type: 'stage'; stat: BattleStat; stages: number; rise: boolean } & BattleSlot)
  /** Statut du monstre (null : guéri). */
  | ({ type: 'status'; status: StatusCondition | null } & BattleSlot)
  | ({ type: 'faint' } & BattleSlot)
  /** Capsule lancée sur le monstre adverse de cette place. */
  | { type: 'capsule'; item: ItemId; place: number; shakes: number; caught: boolean }
  /** Tout ce camp prend la fuite. */
  | { type: 'flee'; target: BattleSide }
  /** Expérience gagnée par ce monstre (de l'équipe du joueur), et ses montées de niveau. */
  | { type: 'xp'; monsterId: number }
  | { type: 'levelup'; level: number; monsterId: number }
  | { type: 'coins'; amount: number }
  | { type: 'badge'; icon: IconName; color: string }
  /** Objet laissé par un monstre vaincu ou donné par un dresseur battu, montré au milieu de l'écran comme un butin de coffre. */
  | { type: 'loot'; item: ItemId; quantity: number };

/** Une étape du récit d'un tour : un texte et, éventuellement, son effet. */
export interface BattleStep {
  text: string;
  effect?: StepEffect;
}

/** Monstre du camp du joueur sur le terrain (le sien, ou celui de son partenaire en coop), avec son état du combat. */
export interface BattleAlly {
  /** Sa fiche, PV du combat compris. */
  monster: OwnedMonsterView;
  /** Énergie restante (sur ENERGY_MAX). */
  energy: number;
  status: StatusCondition | null;
}

export interface BattleSnapshot {
  mode: BattleMode;
  /** Nom du dresseur adverse (duel ou dresseur PNJ). */
  opponent: string | null;
  /** Nom de celui dont on attend le choix (adversaire en duel, partenaire en coop) ; null : rien à attendre. */
  waiting: string | null;
  /** Monstres adverses sur le terrain, par place (null : place vide, monstre K.O. ou capturé). */
  foes: (WildMonsterView | null)[];
  /** Monstres du camp du joueur sur le terrain, par place (null : place vide). */
  allies: (BattleAlly | null)[];
  /** Places dont ce joueur choisit l'action. */
  controlled: number[];
  /** Places dont l'action est déjà choisie pour ce tour. */
  chosen: number[];
  /** Places dont le monstre K.O. doit être remplacé par ce joueur avant le tour suivant. */
  mustSwitch: number[];
  /** Équipe engagée dans le combat. */
  team: OwnedMonsterView[];
  steps: BattleStep[];
  /** Équipe adverse (dresseur ou duel) : nombre de monstres et combien sont encore debout ; null contre un monstre sauvage. */
  foeTeam: TeamCount | null;
  /** Météo de la carte du joueur, montrée dans l'arène. */
  weather: Weather;
}

export type BattleResult = 'win' | 'lose' | 'fled' | 'captured' | 'escaped';

export interface EvolutionView {
  fromName: string;
  fromImage: string;
  toName: string;
  toImage: string;
}

export interface BadgeView {
  id: number;
  name: string;
  icon: IconName;
  color: string;
  description: string;
  /** Champion qui le remet. */
  champion: string | null;
  earnedAt: number | null;
}

export interface BattleRewards {
  coins: number;
  xp: number;
  levelUps: number;
  /** Évolutions du combat (vainqueurs et partage d'expérience), mises en scène l'une après l'autre avant tout le reste. */
  evolutions: EvolutionView[];
  /** Badge remis par le champion vaincu. */
  badge: BadgeView | null;
  /** Attaques que des monstres de l'équipe viennent de pouvoir apprendre sans place libre : le joueur choisit. */
  newMoves: MoveOffer[];
  /** Expérience de chaque monstre de l'équipe (aperçu de fin de combat). */
  team: XpGain[];
}

/** Expérience gagnée par un monstre de l'équipe, et où il en était avant : niveau, expérience dans ce niveau. */
export interface XpGain {
  monsterId: number;
  level: number;
  xp: number;
  xpNext: number;
  gained: number;
}

/** Attaque qu'un monstre peut apprendre, alors qu'il en connaît déjà le maximum : en oublier une, ou passer. */
export interface MoveOffer {
  monsterId: number;
  moveId: number;
}

export interface BattleEnd {
  mode: BattleMode;
  result: BattleResult;
  steps: BattleStep[];
  rewards: BattleRewards | null;
  collection: OwnedMonsterView[];
}

/**
 * Action choisie pour le monstre de la place `place` du terrain. En duel, « flee » signifie abandonner ; capture et
 * objets n'y sont pas permis.
 */
export type BattleAction =
  /** `slot` : attaque connue (0 à 3), ou -1 pour Lutte ; `target` : place du monstre adverse visé. */
  | { type: 'move'; place: number; slot: number; target: number }
  /** À la place d'une attaque : le monstre se repose et récupère une part de son énergie. */
  | { type: 'rest'; place: number }
  | { type: 'capture'; place: number; item: ItemId; target: number }
  /** Objet de soin donné à `monsterId`, sur le terrain ou dans l'équipe. */
  | { type: 'item'; place: number; item: ItemId; monsterId: number }
  | { type: 'flee'; place: number }
  | { type: 'switch'; place: number; id: number }
  /** Revenir sur l'action déjà choisie pour cette place, tant que le tour n'est pas joué. */
  | { type: 'undo'; place: number };

/** Places du terrain par camp : 1 en combat simple, 2 en combat double. */
export const MAX_FIELD_PLACES = 2;

export const NICKNAME_MAX_LENGTH = 16;

/**
 * Organisation des monstres. `move` place un monstre de l'équipe à la position `to` (0 = en tête) ;
 * `toggle` (équipe ↔ boîte) et `release` ne sont possibles que devant un PC de stockage.
 */
export type TeamAction =
  | { action: 'lead' | 'toggle' | 'release'; id: number }
  | { action: 'move'; id: number; to: number }
  | { action: 'rename'; id: number; nickname: string }
  /** Glisser-déposer : le monstre va à cette place de l'équipe ou à cette case de la boîte (échange si elle est prise). */
  | { action: 'place'; id: number; to: { team: number } | { box: number } }
  /** Range toute la boîte dans cet ordre, depuis la première case. */
  | { action: 'sort'; key: BoxSort; descending: boolean };

export interface Inventory {
  coins: number;
  /** Quantité possédée de chaque objet du catalogue (0 pour ceux qui manquent). */
  items: Record<ItemId, number>;
  /** Équipements portés (des objets possédés, d'utilité « équipement »). */
  equipped: ItemId[];
}

/** Monstre de l'équipe d'un autre joueur, tel qu'on le voit en l'inspectant (ni stats ni bonus). */
export interface InspectedMonster {
  name: string;
  speciesName: string;
  image: string;
  level: number;
  element: Element;
  /** Second type (null : il n'en a qu'un). */
  secondElement: Element | null;
  rarity: Rarity;
  shiny: boolean;
}

export interface InspectedPlayer {
  username: string;
  team: InspectedMonster[];
}

/** Ce qui est fait sur ce qui existe dans le jeu. */
export interface ProgressCount {
  done: number;
  total: number;
}

/** Carte du dresseur : la sienne, ou celle d'un autre joueur qu'on examine. */
export interface TrainerCardView {
  username: string;
  /** Date d'inscription. */
  since: number;
  /** Tous les badges du jeu, obtenus (`earnedAt`) ou non. */
  badges: BadgeView[];
  quests: ProgressCount;
  caught: ProgressCount;
  maps: ProgressCount;
  /** Progression générale (0 à 100) : quêtes, espèces capturées, cartes visitées et badges, rapportés au total du jeu. */
  progress: number;
  duelsWon: number;
}

/** Boîte de dialogue d'un PNJ : pages de texte, puis choix éventuels (ex. accepter une quête). */
export interface Dialogue {
  id: number;
  speaker: string;
  /** Voix qui accompagne l'écriture (celle du PNJ, le cri d'un monstre) ; null : silence (pancarte, texte de quête…). */
  voice: Voice | null;
  pages: string[];
  choices: string[] | null;
  /** PNJ qui s'approche du joueur (côté client seulement) avant de parler, puis regagne sa vraie case à la fin du dialogue. */
  approach?: number;
  /**
   * Coop : réplique d'une scène que le partenaire fait défiler ; ce joueur la regarde seulement (pas de réponse
   * attendue), elle se ferme quand le partenaire passe à la suite (`dialogue:close`).
   */
  watchOnly?: boolean;
}

export interface QuestLogEntry {
  id: number;
  name: string;
  description: string;
  giver: string | null;
  /** Ce qu'il faut faire, en une phrase (« Apporter 2 × Potion à Alix ») : l'étape en cours. */
  objective: string;
  /** Étape en cours (à partir de 0) et nombre d'étapes ; étapes déjà faites, en une phrase chacune. */
  step: number;
  steps: number;
  doneSteps: string[];
  status: 'active' | 'completed';
  progress: number;
  target: number;
  /** Récompenses, en une phrase : en cours, celles de l'étape actuelle ; terminée, celles de toutes ses étapes. */
  rewards: string;
  repeat: QuestRepeat;
  /** Nombre de fois où le joueur l'a rendue. */
  timesCompleted: number;
  /** Terminée, et de nouveau proposée par son donneur (répétable, ou quotidienne un autre jour). */
  availableAgain: boolean;
  /**
   * Coop, en rattrapage : ce joueur est plus loin que son partenaire (`partner`) et l'attend ; l'étape montrée est celle
   * du partenaire, `step` celle où il en est lui-même. Null sinon.
   */
  catchingUp: { partner: string; step: number } | null;
}

/** Monstre de départ proposé à un nouveau dresseur (stats au niveau de départ). */
export interface StarterOption {
  id: number;
  name: string;
  description: string;
  element: Element;
  /** Second type (null : il n'en a qu'un). */
  secondElement: Element | null;
  rarity: Rarity;
  image: string;
  level: number;
  stats: { maxHp: number; atk: number; def: number; spd: number };
}

/** Coop en cours : le partenaire (null : on joue seul). */
export interface CoopState {
  partner: { id: number; username: string } | null;
}

export interface DuelInvite {
  fromId: number;
  username: string;
}

/** unknown : jamais rencontré · seen : croisé en combat · caught : capturé au moins une fois. */
export type DexStatus = 'unknown' | 'seen' | 'caught';

export interface DexEntry {
  /** Numéro d'ordre dans le Pokédex (#001…), lignées d'évolution d'un seul tenant (voir dexOrder). */
  number: number;
  speciesId: number;
  status: DexStatus;
  /** Image de l'espèce, une fois vue (null tant qu'elle est inconnue : pas même une silhouette). */
  image: string | null;
  /** Les informations ci-dessous ne sont révélées qu'une fois l'espèce vue. */
  name: string | null;
  rarity: Rarity | null;
  /** Révélée à la première capture. */
  description: string | null;
  element: Element | null;
  /** Révélé avec le premier type (null aussi quand l'espèce n'a qu'un type). */
  secondElement: Element | null;
  /** Évolution connue de l'espèce ; `into` reste masqué tant que l'évolution n'a pas été vue. */
  evolution: { level: number; into: string | null } | null;
  /** Lieux d'apparition (« Zone — Carte »). */
  habitats: string[];
  /** Exemplaires actuellement possédés. */
  owned: number;
  firstSeenAt: number | null;
  firstCaughtAt: number | null;
}

export interface PokedexView {
  entries: DexEntry[];
  seen: number;
  caught: number;
}

export interface ChatMessage {
  id: number;
  username: string;
  text: string;
}

/** Progression du joueur dans une zone : espèces capturées parmi celles qui peuvent y apparaître. */
export interface ZoneProgress {
  caught: number;
  total: number;
  /** Moments où certaines espèces de la zone sont seules à apparaître (jour ou nuit), s'il y en a. */
  times: ('day' | 'night')[];
  /** Météos où certaines espèces de la zone sont seules à apparaître, s'il y en a. */
  weathers: ('clear' | 'rain' | 'snow' | 'fog')[];
}

/** Progression par identifiant de zone, pour les zones de la carte courante. */
export type ZoneProgressMap = Record<number, ZoneProgress>;

export interface WorldSnapshot {
  self: PlayerView;
  map: MapView;
  players: PlayerView[];
  npcs: NpcView[];
  zoneProgress: ZoneProgressMap;
}

/**
 * Anti-triche coupé : un téléporteur de la carte, que le client prend sans attendre le serveur (qui confirme ensuite par
 * map:change, ou le ramène à sa place par self:position s'il refuse).
 */
export interface WarpPreview {
  /** Cases d'entrée, et la case d'arrivée de chacune sur la carte de destination. */
  cells: { x: number; y: number; arrival: Position }[];
  /** Un demi-tour à l'arrivée sur ce téléporteur ramène aussitôt par lui. */
  quickReturn: boolean;
  /** Carte d'arrivée (voir WarpPreviews.destinations). */
  toMap: number;
}

export interface WarpPreviews {
  /** Carte de départ : les aperçus ne valent que sur elle. */
  mapId: number;
  /** Téléporteurs que le joueur peut prendre (pas ceux fermés par un badge qu'il n'a pas ; aucun en coop). */
  warps: WarpPreview[];
  /** Chaque carte d'arrivée telle que le joueur la verra, sans lui-même (il garde sa propre vue). */
  destinations: Record<number, Omit<WorldSnapshot, 'self'>>;
}

export interface ServerToClientEvents {
  /** Connexion : le monde, l'équipe, le sac et la définition de tous les éléments des cartes. */
  init: (data: WorldSnapshot & { collection: OwnedMonsterView[]; inventory: Inventory; elements: MapElement[]; moves: Move[]; characters: CharacterDesign[]; items: Item[]; animations: BattleAnimation[]; sounds: Sound[]; types: ElementType[]; admin: boolean }) => void;
  /** Les attaques ont été modifiées dans l'administration. */
  'moves:update': (moves: Move[]) => void;
  /** La configuration du jeu (nom des créatures…) a été modifiée dans l'administration. */
  'config:update': (config: GameConfig) => void;
  'types:update': (types: ElementType[]) => void;
  /** Les objets ont été modifiés dans l'administration. */
  'items:update': (items: Item[]) => void;
  'sounds:update': (sounds: Sound[]) => void;
  /** Les animations d'attaque ont été modifiées dans l'administration. */
  'animations:update': (animations: BattleAnimation[]) => void;
  /** Les éléments ont été modifiés dans l'administration. */
  'elements:update': (elements: MapElement[]) => void;
  /** Les apparences des personnages ont été modifiées dans l'administration. */
  'characters:update': (characters: CharacterDesign[]) => void;
  'map:change': (data: WorldSnapshot) => void;
  /** Téléporteurs de la carte que le client peut prendre sans attendre (anti-triche coupé) ; vide sinon. */
  'warps:preview': (previews: WarpPreviews) => void;
  'map:update': (data: { map: MapView; position: Position; zoneProgress: ZoneProgressMap; npcs: NpcView[] }) => void;
  'zone:progress': (progress: ZoneProgressMap) => void;
  'player:joined': (player: PlayerView) => void;
  'player:left': (data: { id: number }) => void;
  'player:moved': (player: PlayerView) => void;
  /** Ce joueur caresse son monstre suiveur : un cœur s'élève au-dessus de lui. */
  'player:pet': (data: { id: number }) => void;
  /**
   * Une zone de déclenchement s'est activée : le joueur attend sans bouger (true), puis peut rejouer (false) ;
   * `skippable` : c'est une scène, qu'il peut passer.
   */
  cutscene: (active: boolean, skippable?: boolean) => void;
  /** Un PNJ venu d'ailleurs arrive pour défier le joueur (zone de déclenchement) ; lui seul le voit. */
  'npc:guest': (npc: NpcView) => void;
  /** Le PNJ venu d'ailleurs repart. */
  'npc:gone': (npc: { id: number }) => void;
  /** Un PNJ a fait un pas (ou s'est tourné). */
  /** `stepMs` : durée du pas, pour l'animer au rythme réel du PNJ. */
  'npc:moved': (npc: Position & { id: number; dir: Direction; stepMs: number }) => void;
  /** Une scène fait marcher (ou tourner, même case) le joueur : le pas est animé sur `stepMs`. */
  'self:walk': (step: Position & { dir: Direction; stepMs: number }) => void;
  /** La réplique ouverte est passée (scène passée, ou coop : le partenaire est passé à la suite) : la boîte se ferme. */
  'dialogue:close': () => void;
  /** Scène : effet d'écran (fondu, tremblement, flash) sur `ms` ; `clear` : l'écran redevient normal. */
  'scene:effect': (data: { effect: ScreenEffect | 'clear'; ms: number }) => void;
  /** Une émotion (bulle ❗ ❓…) au-dessus d'un personnage : un PNJ, un acteur de scène ou un joueur (son identifiant). */
  emote: (data: { id: number; emote: Emote }) => void;
  /** Un personnage fait un petit saut sur place (acteur de scène qui prend la parole). */
  hop: (data: { id: number }) => void;
  'self:position': (position: Position) => void;
  'online:count': (count: number) => void;
  notice: (message: string) => void;
  /** Texte de quête à lire (étape atteinte…), une page par entrée : affiché dans la boîte de dialogue, fermée par le joueur. */
  'quest:text': (pages: string[]) => void;
  collection: (monsters: OwnedMonsterView[]) => void;
  chat: (message: ChatMessage) => void;
  'battle:start': (data: BattleSnapshot & { position: Position }) => void;
  'battle:update': (data: BattleSnapshot) => void;
  'battle:end': (data: BattleEnd) => void;
  inventory: (inventory: Inventory) => void;
  /** Comptoir de boutique : les objets vendus sur cette carte. */
  'shop:open': (items: ItemId[]) => void;
  /**
   * Choix de l'apparence : nouveau dresseur (une seule fois, avant le monstre de départ), ou devant une garde-robe,
   * avec l'apparence portée (`current`) : la fenêtre se ferme alors sans rien changer.
   */
  'appearance:choose': (designs: CharacterDesign[], current?: { characterId: number | null; outfit: CharacterOutfit | null }) => void;
  /** Le joueur utilise un PC de stockage : ouverture de la boîte. */
  'storage:open': () => void;
  'duel:invite': (invite: DuelInvite) => void;
  /** Un joueur juste à côté propose de jouer en coop. */
  'coop:invite': (invite: DuelInvite) => void;
  /** Début ou fin de la coop : le partenaire (null : on joue seul). */
  'coop:state': (state: CoopState) => void;
  /** L'équipe vient d'être soignée à une borne. */
  healed: () => void;
  dialogue: (dialogue: Dialogue) => void;
  'npcs:update': (npcs: NpcView[]) => void;
  /** Le joueur a dégagé cet obstacle (objet posé) : il disparaît pour lui, pour de bon. */
  /** Le joueur lance une capacité : l'animation dure `ms`, puis l'effet se produit (obstacle dégagé, changement de carte). */
  'ability:start': (animation: AbilityAnimation) => void;
  /** Le joueur part avec ce véhicule (objet posé) : départ, trajet, puis arrivée (map:change) au bout de `ms`. */
  'vehicle:ride': (ride: VehicleRide) => void;
  'obstacle:cleared': (object: { elementId: number; x: number; y: number }) => void;
  /** Le joueur a poussé ce rocher (objet posé) d'une case : il est là pour lui jusqu'à ce qu'il quitte la carte. */
  'object:pushed': (push: { elementId: number; from: Position; to: Position }) => void;
  /** Il a activé un interrupteur (ou son partenaire, en coop) : ceux qui sont allumés pour lui (voir `MapView.switchesOn`). */
  'map:switches': (switchesOn: string[]) => void;
  /** Le joueur se met à surfer sur ce {monstre} (null : il reprend pied). */
  'self:surf': (mount: FollowerView | null) => void;
  /** Le joueur fouille cet objet à drop : il joue son ouverture, puis disparaît pour lui s'il était à usage unique. */
  'drop:opened': (opened: { elementId: number; x: number; y: number; vanishes: boolean }) => void;
  /** Ce que le joueur vient de trouver en fouillant un objet (montré au milieu de l'écran). */
  'loot:found': (loot: ItemRewards) => void;
  'mapMonsters:update': (monsters: MapMonsterView[]) => void;
  /** Un monstre posé qui se promène (décor) a fait un pas ou s'est tourné. */
  'mapMonster:moved': (monster: Position & { id: string; dir: Direction; stepMs: number }) => void;
  /** Ses quêtes ont changé : les objets posés qu'il peut maintenant utiliser (voir `MapView.usable`). */
  'usable:update': (usable: string[]) => void;
  /** Ses quêtes ont changé : les objets posés qu'il peut maintenant examiner (voir `MapView.inspectable`). */
  'inspectable:update': (inspectable: string[]) => void;
  /** Ses quêtes ont changé : des objets posés qui en dépendent sont apparus ou ont disparu (voir `MapView.objects`). */
  'objects:update': (objects: MapObject[]) => void;
  kicked: (message: string) => void;
  /** Premier passage : le joueur choisit son monstre de départ parmi ces propositions. */
  'starter:choose': (options: StarterOption[]) => void;
}

export interface ClientToServerEvents {
  /** Battement de cœur : le serveur répond tout de suite (le client montre un indicateur s'il tarde). */
  heartbeat: (reply: () => void) => void;
  /**
   * Un pas (ou un simple changement d'orientation) ; `run` : pas couru, à la cadence de course ; `turn` : demi-tour sur
   * place, sans avancer même si la case est libre.
   */
  /** `x`, `y` : la case où le client dit être arrivé (crue seulement quand l'anti-triche est coupé). */
  move: (data: { dir: Direction; run?: boolean; turn?: boolean; x?: number; y?: number }) => void;
  /** Choisit son apparence parmi celles proposées aux joueurs (null : personnage par défaut), et sa tenue si elle est personnalisable. */
  'character:set': (data: { characterId: number | null; outfit?: CharacterOutfit | null }) => void;
  'battle:action': (action: BattleAction) => void;
  team: (action: TeamAction) => void;
  chat: (text: string) => void;
  'pokedex:get': (reply: (pokedex: PokedexView) => void) => void;
  /** Équipe d'un autre joueur connecté (null : il n'est plus là). */
  'player:inspect': (data: { playerId: number }, reply: (player: InspectedPlayer | null) => void) => void;
  /** Carte du dresseur : la sienne (`playerId` null) ou celle d'un autre joueur. */
  'trainer-card:get': (data: { playerId: number | null }, reply: (card: TrainerCardView | null) => void) => void;
  'worldmap:get': (reply: (map: WorldMapView) => void) => void;
  /** S'envoler vers cette ville (le Vol, depuis la carte du monde). */
  fly: (data: { mapId: number }) => void;
  /** Utilise l'objet (borne, comptoir…) ou parle au PNJ situé face au joueur. */
  interact: () => void;
  /** Fin d'une boîte de dialogue ; `choice` est l'index du choix retenu, s'il y en avait. */
  'dialogue:reply': (reply: { id: number; choice: number | null }) => void;
  /** Passe la scène en cours (Échap). */
  'scene:skip': () => void;
  'quests:get': (reply: (quests: QuestLogEntry[]) => void) => void;
  'achievements:get': (reply: (achievements: AchievementView[]) => void) => void;
  'shop:buy': (order: { item: ItemId; quantity: number }) => void;
  /** Utilise une potion hors combat sur un monstre possédé. */
  'item:use': (use: { item: ItemId; monsterId: number }) => void;
  /** Équipe (ou retire) un équipement du sac à dos. */
  'item:equip': (change: { item: ItemId; equipped: boolean }) => void;
  /** Utilise un objet « sur la carte » du sac (Repousse, Corde de sortie). */
  'item:field': (use: { item: ItemId }) => void;
  'duel:challenge': (data: { playerId: number }) => void;
  'duel:respond': (data: { fromId: number; accept: boolean }) => void;
  /** Propose la coop au joueur juste à côté. */
  'coop:invite': (data: { playerId: number }) => void;
  'coop:respond': (data: { fromId: number; accept: boolean }) => void;
  /** Quitte la coop (hors combat). */
  'coop:leave': () => void;
  'starter:pick': (data: { speciesId: number }) => void;
  /** Choisit le monstre qui suit le joueur (null : plus personne). */
  'follower:set': (data: { monsterId: number | null }) => void;
  /** Caresse son monstre suiveur (vu des autres joueurs de la carte). */
  'follower:pet': () => void;
  /** Choisit les attaques d'un monstre (1 à 4, parmi celles qu'il peut connaître à son niveau). */
  'monster:moves': (data: { monsterId: number; moveIds: number[] }) => void;
}

/** Durée des animations de capacités (ms) : le serveur attend ce temps avant l'effet, le client la joue. */
export const ABILITY_ANIMATION_MS = { clear: 700, fly: 900, land: 1700 } as const;

/** Voyage en véhicule : l'objet posé en (x, y), la durée totale et celle des calques « au départ », en ms. */
export interface VehicleRide {
  elementId: number;
  x: number;
  y: number;
  ms: number;
  departMs: number;
}

export type AbilityAnimation =
  | { kind: 'clear'; ms: number; elementId: number; x: number; y: number }
  /** `mount` : le {monstre} qui attrape le joueur par en dessous pour l'élever. */
  | { kind: 'fly'; ms: number; mount: FollowerView }
  /** Atterrissage : le {monstre} dépose le joueur sur la nouvelle carte (le début se joue sous le cercle qui se rouvre). */
  | { kind: 'land'; ms: number; mount: FollowerView };
